import clsx from 'clsx';
import { GripHorizontal, Mic, MicOff, Phone, PhoneOff, ScreenShare, ScreenShareOff, UserPlus, Users, Volume2, X } from 'lucide-react';
import {
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
  type CSSProperties,
  type KeyboardEvent as ReactKeyboardEvent,
  type MouseEvent as ReactMouseEvent,
  type PointerEvent as ReactPointerEvent,
} from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { ApiError } from '../lib/api';
import { useAuth } from '../lib/auth';
import { useCallSession } from '../lib/call-session';
import {
  MicrophoneRefused,
  PARTICIPANT_LABEL,
  callClock,
  isClientParty,
  listSeparator,
  roomIdentity,
  type IncomingCall,
  type LiveCall,
} from '../lib/calls';
import { useI18n } from '../lib/i18n';
import { ScreenStage } from './ScreenStage';
import { Badge, Button, useToast } from './ui';

/**
 * The call on every page (2026-10-29): a call ringing for you, with Accept and
 * Decline, and the call you are in — who is on it, how long, whether you are
 * heard, Mute, Add people and Hang up. Both say, always, that the call is
 * recorded. Top corner on the end side, just under the top bar (operator,
 * 2026-10-29, with a screenshot circling it: "put window on top here in all
 * screens") — until it is moved somewhere else (`useDockPlace`). Over the page;
 * under the top bar's own menus (the bell, the attendance pill, the profile),
 * which open over it, and under any dialog and toast.
 *
 * A screen shared in the call (2026-11-02) is shown beside it, large (`ScreenStage`);
 * the dock shares this tab's own.
 */
export function CallDock() {
  const session = useCallSession();
  const shown = session.incoming.slice(0, 2);
  const open = shown.length > 0 || !!session.current || !!session.ended;
  const dock = useDockPlace(open);
  if (!open) return null;
  return (
    <>
      {session.current && <ScreenStage />}
      <div
        ref={dock.ref}
        style={dock.style}
        onPointerDown={dock.grab}
        onKeyDown={dock.nudge}
        onDoubleClick={dock.putBack}
        className={clsx(
          // The dropdowns' layer, after the page: over everything on it, under the top bar (z-sticky) and its menus.
          'pointer-events-none fixed z-dropdown flex w-[min(23rem,calc(100vw-2rem))] touch-none select-none flex-col gap-3',
          !dock.style && 'top-[calc(theme(spacing.topbar)+1rem)] end-4',
          dock.dragging && 'cursor-grabbing',
        )}
        aria-live="polite"
      >
        {shown.map((call) => (
          <IncomingCard key={call.callId} call={call} more={session.incoming.length - shown.length} />
        ))}
        {session.current && <CurrentCard call={session.current} />}
        {session.ended && !session.current && <EndedCard />}
      </div>
    </>
  );
}

/** Where the dock was moved to: how far from the window's nearer side, and from its nearer top or bottom. */
interface DockPlace {
  side: 'left' | 'right';
  x: number;
  edge: 'top' | 'bottom';
  y: number;
}

/** The window's size, less its scroll bar, the dock's, and where the top bar ends. */
interface Room {
  width: number;
  height: number;
  dockWidth: number;
  dockHeight: number;
  ceiling: number;
}

/**
 * Where it was put, kept per browser. `.v2` since it starts at the top (2026-10-29): a place kept from
 * when it started at the bottom gives way to the new corner once — it can be moved again after.
 */
const PLACE_KEY = 'viganium.callDock.v2';
const OLD_PLACE_KEYS = ['viganium.callDock'];
/** Never nearer the window's edge — nor the top bar — than the corner it starts in (top-4 under the bar, end-4). */
const GAP = 16;
/** How far a press travels before it is a drag and not a click. */
const SLOP = 4;
/** One arrow key on the grip, and one with Shift. */
const STEP = 16;
const LEAP = 64;
/** The grip's arrow keys, as a direction on screen — the same in Arabic. */
const ARROWS: Record<string, [number, number]> = { ArrowLeft: [-1, 0], ArrowRight: [1, 0], ArrowUp: [0, -1], ArrowDown: [0, 1] };
const GRIP = '[data-dock-grip]';
/** A press on these is theirs, not a grab: links, buttons other than the grip, the list that scrolls. */
const NOT_A_GRAB = `a, input, select, textarea, button:not(${GRIP}), [data-no-drag]`;

/**
 * Moving the dock (operator, 2026-10-29: "let me move calling popup to another
 * place while calling"). Dragged by the grip on top of each card, or by any bare
 * part of a card — mouse, finger or pen; the grip's arrow keys move it too, and
 * a double-click on the grip (or its Home key) puts it back in its corner. It
 * keeps to the nearer side and the nearer top or bottom, so a narrower window
 * leaves it where it was put; it never leaves the window, and goes back where
 * it was put when the window is big enough again. Kept in this browser: the
 * next call, and a reload, find it where it was left.
 */
function useDockPlace(open: boolean) {
  const ref = useRef<HTMLDivElement>(null);
  const [place, setPlace] = useState<DockPlace | null>(readPlace);
  const [room, setRoom] = useState<Room | null>(null);
  const [dragging, setDragging] = useState(false);
  const release = useRef<(() => void) | null>(null);
  useEffect(() => () => release.current?.(), []);

  // Measured again whenever the window or the dock changes size: a smaller window, a new card, the people shown.
  useLayoutEffect(() => {
    const dock = ref.current;
    if (!open || !dock) return;
    const measure = () => {
      const { width, height } = frame();
      setRoom({ width, height, dockWidth: dock.offsetWidth, dockHeight: dock.offsetHeight, ceiling: topBarBottom() });
    };
    measure();
    const watch = new ResizeObserver(measure);
    watch.observe(dock);
    window.addEventListener('resize', measure);
    return () => {
      watch.disconnect();
      window.removeEventListener('resize', measure);
    };
  }, [open]);
  const at = place && room ? fitted(place, room) : place;

  const moveTo = (next: DockPlace | null) => {
    setPlace(next);
    keepPlace(next);
  };

  const grab = (e: ReactPointerEvent<HTMLDivElement>) => {
    if (!e.isPrimary || e.button !== 0 || release.current) return;
    if ((e.target as Element).closest(NOT_A_GRAB)) return;
    const box = e.currentTarget.getBoundingClientRect();
    const within = frame();
    const ceiling = topBarBottom();
    const { pointerId, clientX: fromX, clientY: fromY } = e;
    let last: DockPlace | null = null;
    const move = (ev: PointerEvent) => {
      if (ev.pointerId !== pointerId) return;
      if (!last && Math.abs(ev.clientX - fromX) < SLOP && Math.abs(ev.clientY - fromY) < SLOP) return;
      if (!last) setDragging(true);
      last = placeAt(within, ceiling, box.left + ev.clientX - fromX, box.top + ev.clientY - fromY, box.width, box.height);
      setPlace(last);
    };
    const end = (ev: PointerEvent) => {
      if (ev.pointerId !== pointerId) return;
      release.current?.();
      if (last) keepPlace(last);
    };
    // On the window, not the dock: a fast hand outruns it, and a card may go while it is held.
    window.addEventListener('pointermove', move);
    window.addEventListener('pointerup', end);
    window.addEventListener('pointercancel', end);
    release.current = () => {
      window.removeEventListener('pointermove', move);
      window.removeEventListener('pointerup', end);
      window.removeEventListener('pointercancel', end);
      release.current = null;
      setDragging(false);
    };
  };

  const nudge = (e: ReactKeyboardEvent<HTMLDivElement>) => {
    if (!(e.target as Element).closest(GRIP)) return;
    const arrow: [number, number] | undefined = ARROWS[e.key];
    if (e.key === 'Home') {
      e.preventDefault();
      moveTo(null);
    } else if (arrow) {
      e.preventDefault();
      const step = e.shiftKey ? LEAP : STEP;
      const box = e.currentTarget.getBoundingClientRect();
      moveTo(placeAt(frame(), topBarBottom(), box.left + arrow[0] * step, box.top + arrow[1] * step, box.width, box.height));
    }
  };

  const putBack = (e: ReactMouseEvent<HTMLDivElement>) => {
    if ((e.target as Element).closest(GRIP)) moveTo(null);
  };

  const style: CSSProperties | undefined = at
    ? {
        left: at.side === 'left' ? at.x : undefined,
        right: at.side === 'right' ? at.x : undefined,
        top: at.edge === 'top' ? at.y : undefined,
        bottom: at.edge === 'bottom' ? at.y : undefined,
      }
    : undefined;
  return { ref, style, dragging, grab, nudge, putBack };
}

/** The box a fixed element is placed in: the window less its scroll bar, on whichever side that bar is. */
function frame(): DOMRect {
  const probe = document.createElement('div');
  probe.style.cssText = 'position:fixed;top:0;right:0;bottom:0;left:0;visibility:hidden;pointer-events:none';
  document.body.append(probe);
  const box = probe.getBoundingClientRect();
  probe.remove();
  return box;
}

/** Where the top bar ends on screen: the dock is never put under it (nor its menus, which open over the dock). */
function topBarBottom(): number {
  return Math.max(0, document.querySelector('[data-top-bar]')?.getBoundingClientRect().bottom ?? 0);
}

/**
 * The place of a dock this size whose top-left corner goes to (left, top) on screen, kept inside the
 * window and below the top bar (`ceiling`); held to whichever of the top and the bottom it is nearer.
 */
function placeAt(within: DOMRect, ceiling: number, left: number, top: number, width: number, height: number): DockPlace {
  const l = clamp(left - within.left, GAP, within.width - width - GAP);
  const t = clamp(top - within.top, ceiling + GAP, within.height - height - GAP);
  const r = within.width - width - l;
  const b = within.height - height - t;
  return {
    side: l <= r ? 'left' : 'right',
    x: Math.round(Math.min(l, r)),
    edge: t - ceiling <= b ? 'top' : 'bottom',
    y: Math.round(t - ceiling <= b ? t : b),
  };
}

/** The same place, brought inside the window should the window now be too small for it, or the dock too big. */
function fitted(p: DockPlace, room: Room): DockPlace {
  const x = clamp(p.x, GAP, room.width - room.dockWidth - GAP);
  const y =
    p.edge === 'top'
      ? clamp(p.y, room.ceiling + GAP, room.height - room.dockHeight - GAP)
      : clamp(p.y, GAP, room.height - room.dockHeight - room.ceiling - GAP);
  return x === p.x && y === p.y ? p : { ...p, x, y };
}

function clamp(n: number, min: number, max: number) {
  return Math.max(min, Math.min(n, max));
}

function readPlace(): DockPlace | null {
  try {
    for (const old of OLD_PLACE_KEYS) localStorage.removeItem(old);
    const p = JSON.parse(localStorage.getItem(PLACE_KEY) ?? 'null') as Partial<DockPlace> | null;
    const valid =
      (p?.side === 'left' || p?.side === 'right') && (p.edge === 'top' || p.edge === 'bottom') && Number.isFinite(p.x) && Number.isFinite(p.y);
    return valid ? (p as DockPlace) : null;
  } catch {
    return null;
  }
}

function keepPlace(p: DockPlace | null) {
  try {
    if (p) localStorage.setItem(PLACE_KEY, JSON.stringify(p));
    else localStorage.removeItem(PLACE_KEY);
  } catch {
    // Storage refused (a private window, a full quota): it still moves, and forgets on a reload.
  }
}

/** The handle on top of each card. */
function DockGrip() {
  const { t } = useI18n();
  return (
    <button
      type="button"
      data-dock-grip
      aria-label={t('calls.move')}
      title={t('calls.moveHint')}
      className="absolute inset-x-0 top-0 mx-auto grid h-4 w-12 cursor-grab place-items-center rounded-sm text-faint hover:text-[var(--text-primary)] active:cursor-grabbing"
    >
      <GripHorizontal className="h-4 w-4" aria-hidden />
    </button>
  );
}

/** Someone's names, in the reader's language. */
function useName() {
  const { localized } = useI18n();
  return (p: { fullName: string; fullNameAr: string | null } | null | undefined) => (p ? localized(p) : '—');
}

/** The line every call screen carries: the call is recorded. */
function RecordedNotice({ live = false }: { live?: boolean }) {
  const { t } = useI18n();
  return (
    <p className="flex items-center gap-2 text-caption font-medium text-error-fg dark:text-error-dark">
      <span className={clsx('h-2 w-2 shrink-0 rounded-full bg-error', live && 'animate-pulse')} aria-hidden />
      {live ? t('calls.recordingNow') : t('calls.recordedNotice')}
    </p>
  );
}

function IncomingCard({ call, more }: { call: IncomingCall; more: number }) {
  const { t } = useI18n();
  const name = useName();
  const toast = useToast();
  const session = useCallSession();
  const [busy, setBusy] = useState<'accept' | 'decline' | null>(null);
  const run = async (what: 'accept' | 'decline') => {
    setBusy(what);
    try {
      if (what === 'accept') await session.accept(call.callId);
      else await session.decline(call.callId);
    } catch (e) {
      toast.error(callErrorText(e, t));
    } finally {
      setBusy(null);
    }
  };
  // A client calling from the portal says so before anything else (2026-11-01).
  const kind = call.underway
    ? t('calls.joinUnderway')
    : isClientParty(call.caller)
      ? t('calls.fromClient')
      : call.type === 'GROUP'
        ? t('calls.groupVoiceCall')
        : t('calls.voiceCall');
  return (
    <section
      role="alertdialog"
      aria-label={t('calls.incomingFrom', { name: name(call.caller) })}
      className="pointer-events-auto surface relative rounded-lg p-4 shadow-lg ring-1 ring-primary/40"
    >
      <DockGrip />
      <div className="flex items-center gap-3">
        <span className="grid h-10 w-10 shrink-0 place-items-center rounded-full bg-primary/15 text-primary animate-pulse">
          <Phone className="h-5 w-5" aria-hidden />
        </span>
        <div className="min-w-0">
          <p className="truncate text-title text-[var(--text-primary)]">{name(call.caller)}</p>
          <p className="text-body-sm text-muted">
            {kind}
            {call.people > 2 && ` · ${t('calls.peopleCount', { n: call.people })}`}
          </p>
        </div>
      </div>
      <div className="mt-3">
        <RecordedNotice />
      </div>
      <div className="mt-3 flex gap-2">
        <Button className="flex-1" variant="danger" loading={busy === 'decline'} disabled={!!busy} onClick={() => void run('decline')}>
          <PhoneOff className="h-4 w-4" aria-hidden />
          {t('calls.decline')}
        </Button>
        <Button
          className="flex-1 !bg-success-fg !text-white hover:brightness-110"
          loading={busy === 'accept'}
          disabled={!!busy}
          onClick={() => void run('accept')}
        >
          <Phone className="h-4 w-4" aria-hidden />
          {t('calls.accept')}
        </Button>
      </div>
      {more > 0 && <p className="mt-2 text-caption text-muted">{t('calls.moreRinging', { n: more })}</p>}
    </section>
  );
}

function CurrentCard({ call }: { call: LiveCall }) {
  const { t, locale } = useI18n();
  const name = useName();
  const toast = useToast();
  const navigate = useNavigate();
  const session = useCallSession();
  const { user } = useAuth();
  const [now, setNow] = useState(() => Date.now());
  const [leaving, setLeaving] = useState(false);
  const [showPeople, setShowPeople] = useState(false);
  useEffect(() => {
    const tick = window.setInterval(() => setNow(Date.now()), 1000);
    return () => window.clearInterval(tick);
  }, []);

  const others = call.participants.filter((p) => p.user.id !== user?.id);
  const present = call.participants.filter((p) => p.status === 'ACCEPTED');
  const title =
    others
      .filter((p) => p.status === 'ACCEPTED' || p.status === 'RINGING')
      .map((p) => name(p.user))
      .join(listSeparator(locale)) || name(call.caller);
  const serverNow = now + session.serverOffset;
  const elapsed = call.startedAt ? (serverNow - Date.parse(call.startedAt)) / 1000 : null;

  const state = (() => {
    if (!call.me.thisDevice) return t('calls.state.otherDevice');
    if (call.status === 'RINGING') return t('calls.state.calling');
    if (call.status !== 'PREPARING' && call.status !== 'ACTIVE') return t('calls.state.ending');
    // Not in the room yet, or lost from it: never a running clock for a call this tab cannot hear.
    if (session.connection === 'reconnecting') return t('calls.state.reconnecting');
    if (session.connection !== 'connected') return t('calls.state.connecting');
    if (call.status === 'PREPARING') return t('calls.state.preparing');
    return elapsed !== null ? callClock(elapsed) : t('calls.state.connecting');
  })();

  const hangUp = async () => {
    setLeaving(true);
    try {
      await session.hangUp();
    } catch (e) {
      toast.error(callErrorText(e, t));
    } finally {
      setLeaving(false);
    }
  };

  // A screen, a window or a tab: the browser asks which. Nothing chosen is nothing shared, and nothing to say.
  const [choosing, setChoosing] = useState(false);
  const share = async () => {
    if (session.sharing) return session.stopSharing();
    setChoosing(true);
    try {
      await session.startSharing();
    } catch {
      toast.error(t('calls.share.failed'));
    } finally {
      setChoosing(false);
    }
  };

  return (
    <section aria-label={t('calls.currentCall')} className="pointer-events-auto surface relative rounded-lg p-4 shadow-lg ring-1 ring-[var(--border-subtle)]">
      <DockGrip />
      <div className="flex items-start gap-3">
        <span className="grid h-10 w-10 shrink-0 place-items-center rounded-full bg-success/15 text-success-fg dark:text-success">
          {call.type === 'GROUP' ? <Users className="h-5 w-5" aria-hidden /> : <Phone className="h-5 w-5" aria-hidden />}
        </span>
        <div className="min-w-0 flex-1">
          <Link to={`/calls/${call.id}`} className="block truncate text-title text-[var(--text-primary)] hover:underline">
            {title}
          </Link>
          <p className="text-body-sm text-muted tabular" aria-live="polite">
            {state}
            {present.length > 2 && ` · ${t('calls.peopleCount', { n: present.length })}`}
          </p>
        </div>
        {/* Not a control: the call is recorded, whatever anyone does. */}
        <Badge tone="error" dot className="shrink-0">
          {t('calls.recordingBadge')}
        </Badge>
      </div>

      <div className="mt-3">
        <RecordedNotice live={call.status === 'ACTIVE'} />
      </div>
      {call.endingSoon && <p className="mt-2 text-caption text-warning-fg dark:text-warning">{t('calls.endingSoon')}</p>}
      {session.micDenied && call.me.thisDevice && (
        <p className="mt-2 text-caption text-error-fg dark:text-error-dark">{t('calls.micDenied')}</p>
      )}
      {session.sharing && (
        <p className="mt-2 flex items-center gap-2 text-caption font-medium text-warning-fg dark:text-warning">
          <ScreenShare className="h-4 w-4 shrink-0" aria-hidden />
          {t('calls.share.yours')}
        </p>
      )}
      {session.audioBlocked && (
        <Button size="sm" className="mt-2 w-full" onClick={session.enableAudio}>
          <Volume2 className="h-4 w-4" aria-hidden />
          {t('calls.enableAudio')}
        </Button>
      )}

      {(showPeople || call.type === 'GROUP') && (
        <ul data-no-drag className="mt-3 max-h-40 space-y-1 overflow-y-auto">
          {call.participants.map((p) => (
            <li key={p.user.id} className="flex items-center justify-between gap-2 text-body-sm">
              <span className="flex min-w-0 items-center gap-1.5">
                <span
                  className={clsx(
                    'h-1.5 w-1.5 shrink-0 rounded-full',
                    session.speaking.has(roomIdentity(p.user)) ? 'bg-success' : p.inRoom ? 'bg-success/50' : 'bg-[var(--text-tertiary)]',
                  )}
                  aria-hidden
                />
                <span className="truncate text-[var(--text-primary)]">{name(p.user)}</span>
              </span>
              <span className="shrink-0 text-caption text-muted">
                {p.status === 'ACCEPTED' ? (p.inRoom ? t('calls.inCall') : t('calls.joining')) : t(PARTICIPANT_LABEL[p.status])}
              </span>
            </li>
          ))}
        </ul>
      )}

      <div className="mt-3 flex items-center gap-2">
        {call.me.thisDevice && (
          <Button
            size="sm"
            onClick={session.toggleMute}
            aria-pressed={session.muted}
            disabled={call.status !== 'ACTIVE'}
            title={session.muted ? t('calls.unmute') : t('calls.mute')}
          >
            {session.muted ? <MicOff className="h-4 w-4" aria-hidden /> : <Mic className="h-4 w-4" aria-hidden />}
            {session.muted ? t('calls.unmute') : t('calls.mute')}
          </Button>
        )}
        {/* A desktop browser's: a phone's cannot share a screen. Only once the call is recorded, as the microphone. */}
        {call.me.thisDevice && session.canShareScreens && (
          <Button
            size="sm"
            // Amber while it shares — never the red of Hang up beside it.
            variant={session.sharing ? 'signal' : 'secondary'}
            onClick={() => void share()}
            aria-pressed={session.sharing}
            loading={choosing}
            disabled={!session.sharing && !session.mayShare}
            title={session.sharing ? t('calls.share.stop') : t('calls.share.start')}
          >
            {session.sharing ? <ScreenShareOff className="h-4 w-4" aria-hidden /> : <ScreenShare className="h-4 w-4" aria-hidden />}
            <span className="sr-only">{session.sharing ? t('calls.share.stop') : t('calls.share.start')}</span>
          </Button>
        )}
        {call.me.mayAddPeople && call.me.thisDevice && (
          <Button size="sm" onClick={() => navigate('/calls/add')} title={t('calls.addPeople')}>
            <UserPlus className="h-4 w-4" aria-hidden />
            <span className="sr-only">{t('calls.addPeople')}</span>
          </Button>
        )}
        {call.type !== 'GROUP' && (
          <Button size="sm" variant="ghost" onClick={() => setShowPeople((s) => !s)} aria-expanded={showPeople} title={t('calls.people')}>
            <Users className="h-4 w-4" aria-hidden />
            <span className="sr-only">{t('calls.people')}</span>
          </Button>
        )}
        <Button size="sm" variant="danger" className="ms-auto" loading={leaving} onClick={() => void hangUp()}>
          <PhoneOff className="h-4 w-4" aria-hidden />
          {call.status === 'RINGING' && call.me.isCaller ? t('calls.cancel') : t('calls.hangUp')}
        </Button>
      </div>
    </section>
  );
}

function EndedCard() {
  const { t } = useI18n();
  const session = useCallSession();
  if (!session.ended) return null;
  const failed = session.ended.text !== 'calls.ended.normal';
  return (
    <section className={clsx('pointer-events-auto surface relative rounded-lg p-4 shadow-lg ring-1', failed ? 'ring-error/40' : 'ring-[var(--border-subtle)]')}>
      <DockGrip />
      <div className="flex items-start gap-3">
        <PhoneOff className={clsx('mt-0.5 h-5 w-5 shrink-0', failed ? 'text-error-fg dark:text-error-dark' : 'text-muted')} aria-hidden />
        <p className="flex-1 text-body-sm text-[var(--text-primary)]">{t(session.ended.text)}</p>
        <button type="button" onClick={session.dismissEnded} className="rounded-sm p-1 text-muted hover:text-[var(--text-primary)]" aria-label={t('common.close')}>
          <X className="h-4 w-4" />
        </button>
      </div>
      <Link to={`/calls/${session.ended.callId}`} className="mt-2 inline-block text-caption text-primary-600 hover:underline dark:text-primary-400">
        {t('calls.openCall')}
      </Link>
    </section>
  );
}

/** A call refused, said in the reader's language. */
export function callErrorText(e: unknown, t: ReturnType<typeof useI18n>['t']): string {
  if (e instanceof MicrophoneRefused) return e.reason === 'blocked' ? t('calls.error.micBlocked') : t('calls.error.micMissing');
  if (!(e instanceof ApiError)) return e instanceof Error ? e.message : String(e);
  const reason = (e.details as { reason?: string } | undefined)?.reason;
  switch (e.code) {
    case 'CALLS_UNAVAILABLE':
      return t('calls.error.unavailable');
    case 'ALREADY_IN_CALL':
      return t('calls.error.alreadyInCall');
    case 'CALL_FULL':
      return t('calls.error.full', { n: (e.details as { max?: number } | undefined)?.max ?? '' });
    case 'CALL_OTHER_DEVICE':
      return t('calls.error.otherDevice');
    case 'CALL_INVITATION_CLOSED':
      return reason === 'ANSWERED_ELSEWHERE'
        ? t('calls.error.answeredElsewhere')
        : reason === 'DECLINED'
          ? t('calls.error.declined')
          : t('calls.error.noLongerRinging');
    default:
      return e.message;
  }
}
